import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import axiosInstance from "src/store/features/auth/axiosCSRF";
import { toApiError } from "src/types/api";
import { workFlowRunAPI } from "./workFlowRunSlice";

export interface iWorkFlowResultState {
    response: any;
    drugScoreResponse: any;
    sortedResponse: any;
    validationReportResponse: any;
    predictionsResponse: any;
    pdbResponse: any;
    resultByFileName: Record<string, any>;
    loadingByFileName: Record<string, boolean>;
    errorByFileName: Record<string, { message: string | null; status: number | null } | null>;
    selectedTaskName: string | null;
    isLoading: boolean;
    isDrugScoreLoading: boolean;
    isSortedLoading: boolean;
    message: string | null;
    status: number | null;
    error: string | null;
    errorStatus: number | null;
    lastPdbUpdate: number;
    resultAPIcalled: boolean;  // 추가
}

const initialState: iWorkFlowResultState = {
    response: null,
    drugScoreResponse: null,
    sortedResponse: null,
    validationReportResponse: null,
    predictionsResponse: null,
    pdbResponse: null,
    resultByFileName: {},
    loadingByFileName: {},
    errorByFileName: {},
    selectedTaskName: null,
    isLoading: false,
    isDrugScoreLoading: false,
    isSortedLoading: false,
    message: "",
    status: null,
    error: null,
    errorStatus: null,
    lastPdbUpdate: 0,
    resultAPIcalled: false,  // 추가
};

const clearWorkflowResultState = (state: iWorkFlowResultState) => {
    state.response = null;
    state.drugScoreResponse = null;
    state.sortedResponse = null;
    state.validationReportResponse = null;
    state.predictionsResponse = null;
    state.pdbResponse = null;
    state.status = null;
    state.message = "";
    state.isLoading = false;
    state.isDrugScoreLoading = false;
    state.isSortedLoading = false;
    state.resultByFileName = {};
    state.loadingByFileName = {};
    state.errorByFileName = {};
    state.error = null;
    state.errorStatus = null;
    state.lastPdbUpdate = 0;
    state.selectedTaskName = null;
    state.resultAPIcalled = false;
};

export interface WorkflowResultParams {
    group_name: string;
    library_name: string;
    round: number;
    step: number;
    workflow_name: string;
    task_name?: string;
    extension?: string;
    backup?: boolean;
    timestamp?: string;
    saveToPdbResponse?: boolean;
    file_name?: string;
}

export const workFlowResultAPI = createAsyncThunk(
    "workFlowResultAPI",
    async (data: WorkflowResultParams, { rejectWithValue }) => {
        try {
            const djangoEndpoint = getDjangoEndpoint();
            const pathParam = '/workflow/result/';

            const requestData: any = {
                group_name: data.group_name,
                library_name: data.library_name,
                workflow_name: data.workflow_name,
                round: data.round,
                step: data.step,
            };

            if (data.task_name !== undefined) requestData.task_name = data.task_name;
            if (data.extension !== undefined) requestData.extension = data.extension;
            if (data.file_name !== undefined) requestData.file_name = data.file_name;
            if (data.backup !== undefined) requestData.backup = data.backup;
            if (data.timestamp !== undefined) requestData.timestamp = data.timestamp;

            const response = await axiosInstance.post(`${djangoEndpoint}${pathParam}`, requestData);

            return {
                saveToPdbResponse: data.saveToPdbResponse || false,
                fileName: data.file_name || null,
                taskName: data.task_name || null,
                data: response.data,
                status: response.status,
            };
        } catch (error) {
            const apiError = toApiError(error);
            const errorStatus = apiError.response?.status || null;
            const errorMessage = apiError.response?.data?.error || apiError.message || "An error occurred";

            return rejectWithValue({
                message: errorMessage,
                status: errorStatus,
                fileName: data.file_name || null,
                taskName: data.task_name || null,
            });
        }
    }
);

export const workFlowResultSlice = createSlice({
    name: "workFlowResult",
    initialState,
    reducers: {
        setPdbResponse: (state, action: PayloadAction<any>) => {
            if (typeof action.payload === 'string') {
                state.pdbResponse = action.payload;
            } else {
                state.pdbResponse = JSON.parse(JSON.stringify(action.payload));
            }
            state.lastPdbUpdate = Date.now();
        },
        resetWorkflowResult: (state) => {
            clearWorkflowResultState(state);
        },
        resetPdbResponse: (state) => {
            state.pdbResponse = null;
            state.lastPdbUpdate = 0;
        },
        setSelectedWorkflowResultTaskName: (state, action: PayloadAction<string | null>) => {
            state.selectedTaskName = action.payload;
        },
        setResultAPIcalled: (state) => {
            state.resultAPIcalled = true;
          },
          
          // 워크플로우 재실행 시 초기화용
          resetResultAPIcalled: (state) => {
            state.resultAPIcalled = false;
          },
    },
    extraReducers: (builder) => {
        builder
            .addCase(workFlowRunAPI.pending, (state, action) => {
                clearWorkflowResultState(state);
            })
            .addCase(workFlowResultAPI.pending, (state, action) => {
                const fileName = action.meta.arg.file_name;

                if (fileName) {
                    state.loadingByFileName[fileName] = true;
                    state.errorByFileName[fileName] = null;
                }

                if (fileName === 'drug_score_results.csv') {
                    state.isDrugScoreLoading = true;
                } else if (fileName === 'sorted_results.csv') {
                    state.isSortedLoading = true;
                } else {
                    state.isLoading = true;
                }

                state.error = null;
                state.errorStatus = null;
            })
            .addCase(workFlowResultAPI.fulfilled, (state, action) => {
                state.status = action.payload.status;
                state.error = null;
                state.errorStatus = null;
                const fileName = action.payload.fileName;
                const taskName = action.payload.taskName;
                const compositeFileName = taskName && fileName ? `${taskName}:${fileName}` : fileName;

                if (compositeFileName) {
                    state.resultByFileName[compositeFileName] = action.payload.data;
                }

                if (fileName) {
                    state.loadingByFileName[fileName] = false;
                    state.errorByFileName[fileName] = null;
                }

                if (action.payload.saveToPdbResponse) {
                    state.pdbResponse = typeof action.payload.data === 'string'
                        ? action.payload.data
                        : JSON.stringify(action.payload.data);
                    state.lastPdbUpdate = Date.now();
                } else if (fileName === 'drug_score_results.csv') {
                    state.drugScoreResponse = action.payload.data;
                    state.isDrugScoreLoading = false;
                } else if (fileName === 'sorted_results.csv') {
                    state.sortedResponse = action.payload.data;
                    state.isSortedLoading = false;
                } else if (fileName === 'validation_report.json') {
                    const shouldSetValidation =
                        !state.selectedTaskName || state.selectedTaskName === taskName;

                    if (shouldSetValidation) {
                        state.validationReportResponse = action.payload.data;
                        state.resultByFileName['validation_report.json'] = action.payload.data;
                        if (!state.selectedTaskName) {
                            state.selectedTaskName = taskName;
                        }
                    }

                    state.isLoading = false;
                } else if (fileName === 'predictions.json') {
                    const shouldSetPredictions =
                        !state.selectedTaskName || state.selectedTaskName === taskName;

                    if (shouldSetPredictions) {
                        state.predictionsResponse = action.payload.data;
                        state.resultByFileName['predictions.json'] = action.payload.data;
                        if (!state.selectedTaskName) {
                            state.selectedTaskName = taskName;
                        }
                    }

                    state.isLoading = false;
                } else {
                    state.response = action.payload.data;
                    state.isLoading = false;
                }
            })
            .addCase(workFlowResultAPI.rejected, (state, action) => {
                const fileName = (action.meta.arg as WorkflowResultParams).file_name;
                const payload =
                    typeof action.payload === 'object' && action.payload !== null
                        ? (action.payload as any)
                        : null;

                if (fileName) {
                    state.loadingByFileName[fileName] = false;
                    state.errorByFileName[fileName] = {
                        message: payload?.message || (action.payload as string) || "An error occurred",
                        status: payload?.status || null,
                    };
                }

                if (fileName === 'drug_score_results.csv') {
                    state.isDrugScoreLoading = false;
                } else if (fileName === 'sorted_results.csv') {
                    state.isSortedLoading = false;
                } else {
                    state.isLoading = false;
                }

                if (payload) {
                    state.error = payload.message || "An error occurred";
                    state.errorStatus = payload.status || null;
                    state.message = payload.message || "An error occurred";
                    state.status = payload.status || null;
                } else {
                    state.error = action.payload as string || "An error occurred";
                    state.message = action.payload as string || "An error occurred";
                }
            });
    },
});

export const { setPdbResponse, resetWorkflowResult, resetPdbResponse, setSelectedWorkflowResultTaskName, setResultAPIcalled, resetResultAPIcalled } = workFlowResultSlice.actions;
export default workFlowResultSlice.reducer;
